import {
  getFacilityCategories,
  groupByFloor,
  type HaNFacilityCategory,
  HaNViewStoreContext,
  selectFacilityCategoryId,
  useHaNView,
} from "@lib/logic";
import { getAllMeetingRoomSuggestionsUnfilteredForRequest, getFloorsByBuildingId, useAppSelector } from "@lib/store";
import { useRequiredContext } from "@lib/utils";

import { useRoomsRequest } from "../rooms/useRoomsRequest";
import { HaNContext } from "../shell/HaNContext";
import type { FacilityCategoryEntry } from "./types";

const NO_IDS: ReadonlySet<string> = new Set();

/**
 * The building's facility categories, and the open one's entries on the selected floor and on each other floor. Without
 * an open category, the entries are all of the building's facilities, for the carousel.
 */
export const useFacilitiesModel = () => {
  const { isTabLoading, building, floor, facilities, resources, filterData, showsRooms, showsAreas } =
    useRequiredContext(HaNContext);
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const floors = useAppSelector(state => getFloorsByBuildingId(state, building.buildingId ?? ""));
  const request = useRoomsRequest();
  const rooms = useAppSelector(state => getAllMeetingRoomSuggestionsUnfilteredForRequest(state, request));
  const categoryId = useHaNView(selectFacilityCategoryId);

  const categories = getFacilityCategories({
    facilities,
    hasAreas: showsAreas && resources.some(r => r.type === "area"),
    hasRooms: showsRooms && resources.some(r => r.type === "room"),
  });

  // A category the building doesn't have, like after changing building, shows the categories again
  const category = categories.find(c => c.id === categoryId) ?? null;

  const getEntries = (open: HaNFacilityCategory | null): FacilityCategoryEntry[] => {
    switch (open?.kind) {
      case "area":
        return filterData.workspaces.allData.map(area => {
          return { kind: "area", id: area.id, floorId: area.floorId, name: area.name, area };
        });
      case "room":
        return rooms.map(room => {
          return { kind: "room", id: room.id, floorId: room.floorId, name: room.displayName, room };
        });
      default:
        return facilities
          .filter(f => !open || f.data.facilityType.id === open.id)
          .map(facility => {
            return { kind: "facility", id: facility.id, floorId: facility.floorId, name: facility.name, facility };
          });
    }
  };

  const entries = getEntries(category).toSorted((a, b) => a.name.localeCompare(b.name));

  const { onSelectedFloor, otherFloors } = groupByFloor({
    items: entries,
    floorIds: floors.map(({ id }) => id),
    selectedFloorId: floor.floorId,
  });

  return {
    isLoading: isTabLoading,
    categories,
    category,
    openCategory: (id: string | null) => viewStore.selectFacilityCategory(id),
    entriesOnFloor: onSelectedFloor,
    otherFloors,
    highlightedIds: category ? new Set(entries.map(({ id }) => id)) : NO_IDS,
  };
};
